import { FC, useMemo, useState } from 'react';

import { PublicFeedEvent } from '@shared/types/event';
import dayjs from 'dayjs';
import { uniq, uniqBy } from 'lodash';

import {
  useFinalizeDynamicEventMutation,
  useRejectDynamicEventMutation,
} from '@admin/mutations/AdminDynamicEventsMutations';
import { useUpdateEventImageMutation } from '@admin/mutations/AdminEventsMutations';
import { useSourceEventIdsQuery } from '@admin/queries/AdminEventsQueries';

import { getApiUrl } from '@api/API';

import { useUnauthorizedEventsQuery } from '@queries/EventQueries';
import { useSeries } from '@queries/TagQueries';

import Button from '@components/buttons/Button';
import IconButton from '@components/buttons/IconButton';
import { MultipleChoiceList } from '@components/form/MultipleChoiceList';
import { FontAwesomeIcon } from '@components/icons/FontAwesomeIcon';
import Card from '@components/layout/Card/Card';
import { EmptyState } from '@components/layout/EmptyState/EmptyState';
import { HorizontalScroll } from '@components/layout/HorizontalScroll/HorizontalScroll';

// The rendered card image for a dynamic event, as opposed to a source event's thumbnail
const DYNAMIC_IMAGE_ID = 'dynamic';

// TODO: replace with a real mutation
const approveEventStub = async (payload: { eventId: string; sourceEventIds: string[] }) => {
  console.log('approveEventStub', payload);
};

const getEventThumbnailUrl = (eventId: string, cacheBust?: number) =>
  `${getApiUrl()}/v1/events/${eventId}/thumbnail${cacheBust ? `?v=${cacheBust}` : ''}`;

const withCacheBust = (url: string, cacheBust: number) =>
  cacheBust ? `${url}${url.includes('?') ? '&' : '?'}v=${cacheBust}` : url;

interface ImageOption {
  id: string;
  src: string;
}

interface EventImagePickerProps {
  title: string;
  imageOptions: ImageOption[];
  selectedId?: string;
  onSelect: (id: string) => void;
}

// Always a scrollable strip, even for one image, so every source's image stays visible
const EventImagePicker: FC<EventImagePickerProps> = ({
  title,
  imageOptions,
  selectedId,
  onSelect,
}) => (
  <HorizontalScroll
    items={[
      <div className="d-flex gap-2">
        {imageOptions.map(({ id, src }) => (
          <div key={id} onClick={() => onSelect(id)}>
            <img
              src={src}
              alt={title}
              height={160}
              style={{
                borderRadius: 8,
                border: selectedId === id ? '4px solid var(--color-green)' : 'none',
              }}
            />
          </div>
        ))}
      </div>,
    ]}
  />
);

interface UpdateEventImageFormProps {
  event: PublicFeedEvent;
  sourceEventIds: string[];
  onUpdated: () => void;
}

const UpdateEventImageForm: FC<UpdateEventImageFormProps> = ({
  event,
  sourceEventIds,
  onUpdated,
}) => {
  const { isUpdatingEventImage, updateEventImageAsync } = useUpdateEventImageMutation();
  // Nothing is preselected, since the current image may have come from any source
  const [imageEventId, setImageEventId] = useState<string>();
  // Captured once, so thumbnails stay cached while selecting
  const [loadedAt] = useState<number>(() => Date.now());

  const imageOptions = useMemo(
    () => sourceEventIds.map((id) => ({ id, src: getEventThumbnailUrl(id, loadedAt) })),
    [sourceEventIds, loadedAt],
  );

  const handleUpdateImage = async () => {
    if (!imageEventId) {
      return;
    }

    await updateEventImageAsync({ eventId: event._id, imageFromEventId: imageEventId });
    setImageEventId(undefined);
    onUpdated();
  };

  return (
    <div>
      <EventImagePicker
        title={event.title}
        imageOptions={imageOptions}
        selectedId={imageEventId}
        onSelect={setImageEventId}
      />
      <div className="mt-2">
        <Button
          roundness="rounded"
          disabled={!imageEventId || isUpdatingEventImage}
          onClick={handleUpdateImage}
        >
          Update image
        </Button>
      </div>
    </div>
  );
};

enum DynamicEventActionCardAction {
  Approve = 'approve',
  Reject = 'reject',
}

const ACTION_OPTIONS = [
  { value: DynamicEventActionCardAction.Approve, label: 'Approve this event' },
  { value: DynamicEventActionCardAction.Reject, label: 'Reject this event' },
];

type SeriesOption = { label: string; value: string };

interface ApproveEventFormProps {
  event: PublicFeedEvent;
  actionToken: string;
  isDynamic: boolean;
  sourceEventIds: string[];
  tagIds: string[];
  seriesOptions: SeriesOption[];
  onComplete: () => void;
}

const ApproveEventForm: FC<ApproveEventFormProps> = ({
  event,
  actionToken,
  isDynamic,
  sourceEventIds,
  tagIds,
  seriesOptions,
  onComplete,
}) => {
  const { isFinalizingDynamicEvent, finalizeDynamicEventAsync } = useFinalizeDynamicEventMutation();

  const imageOptions = useMemo(
    () => [
      ...(isDynamic && event.img ? [{ id: DYNAMIC_IMAGE_ID, src: event.img }] : []),
      ...sourceEventIds.map((id) => ({ id, src: getEventThumbnailUrl(id) })),
    ],
    [isDynamic, event.img, sourceEventIds],
  );
  const [imageEventId, setImageEventId] = useState<string>(imageOptions[0]?.id);

  const entertainerOptions = useMemo(
    () =>
      uniqBy(
        (event.entertainers ?? []).map((entertainer) => ({
          // Placeholders on dynamic events have no doc, so fall back to the title
          label: entertainer._id ? entertainer.title : `${entertainer.title} (No profile)`,
          value: entertainer._id || entertainer.title,
        })),
        'value',
      ),
    [event.entertainers],
  );
  const [claimedEntertainerIds, setClaimedEntertainerIds] = useState<string[]>(
    entertainerOptions.map(({ value }) => value),
  );

  // Only preselect series that are shown, so Finalize never submits a hidden selection
  const [seriesIds, setSeriesIds] = useState<string[]>(() => {
    const optionValues = new Set(seriesOptions.map(({ value }) => value));
    return uniq([...tagIds, ...(event.series ?? []).map(({ _id }) => _id)]).filter((id) =>
      optionValues.has(id),
    );
  });

  const handleFinalize = async () => {
    await finalizeDynamicEventAsync({
      actionToken,
      imageFromEventId: imageEventId,
      tagIds: seriesIds,
      // Placeholders are keyed by title and have no doc to attach
      entertainerIds: claimedEntertainerIds.filter((id) => event.entertainerIds.includes(id)),
    });
    onComplete();
  };

  return (
    <div className="mt-4">
      <EventImagePicker
        title={event.title}
        imageOptions={imageOptions}
        selectedId={imageEventId}
        onSelect={setImageEventId}
      />

      {!!entertainerOptions.length && (
        <div className="mt-2">
          <div style={{ fontSize: 12, fontWeight: 500 }}>Entertainers</div>
          <MultipleChoiceList
            isMultiSelect
            options={entertainerOptions}
            defaultSelected={claimedEntertainerIds}
            onChange={setClaimedEntertainerIds}
          />
        </div>
      )}

      {!!seriesOptions.length && (
        <div className="mt-2">
          <div style={{ fontSize: 12, fontWeight: 500 }}>Series</div>
          <MultipleChoiceList
            isMultiSelect
            options={seriesOptions}
            defaultSelected={seriesIds}
            onChange={setSeriesIds}
          />
        </div>
      )}

      <div className="mt-4">
        <Button roundness="rounded" disabled={isFinalizingDynamicEvent} onClick={handleFinalize}>
          Finalize
        </Button>
      </div>
    </div>
  );
};

interface DynamicEventActionCardProps {
  event: PublicFeedEvent;
  tagIds: string[];
  seriesOptions: SeriesOption[];
  /** Events already merged into this one, from the super-admin endpoint */
  mergedSourceEventIds?: string[];
}

const DynamicEventActionCard: FC<DynamicEventActionCardProps> = ({
  event,
  tagIds,
  seriesOptions,
  mergedSourceEventIds,
}) => {
  const [isRejected, setIsRejected] = useState<boolean>(false);
  const [isFinalized, setIsFinalized] = useState<boolean>(false);
  const [imageCacheBust, setImageCacheBust] = useState<number>(0);
  const { isRejectingDynamicEvent, rejectDynamicEventAsync } = useRejectDynamicEventMutation();
  const [selectedAction, setSelectedAction] = useState<DynamicEventActionCardAction>(
    DynamicEventActionCardAction.Approve,
  );

  // Listed events can carry sourceEventIds too (pending siblings), so only the token marks a dynamic event
  const isDynamic = !!event.actionToken;
  const sourceEventIds = useMemo(
    () =>
      isDynamic && event.sourceEventIds?.length
        ? event.sourceEventIds
        : uniq([event._id, ...(event.sourceEventIds ?? []), ...(mergedSourceEventIds ?? [])]),
    [isDynamic, event.sourceEventIds, event._id, mergedSourceEventIds],
  );

  // Only dynamic events carry a token, so only they can be finalized or rejected here
  const { actionToken } = event;
  // A finalized event's sources have already changed status, so the token has nothing left to act on
  const canAct = !!actionToken && !isFinalized;
  const isApproved = isFinalized || event.isApproved;

  // The card stays as an approved event, and the avatar reloads in case the image changed
  const handleFinalized = () => {
    setIsFinalized(true);
    setImageCacheBust(Date.now());
  };

  const handleReject = async () => {
    if (!actionToken) {
      return;
    }

    await rejectDynamicEventAsync({ actionToken, reason: '' });
    setIsRejected(true);
  };

  const handleSelectAction = async (action: DynamicEventActionCardAction) => {
    setSelectedAction(action);
    if (action === DynamicEventActionCardAction.Approve) {
      await approveEventStub({ eventId: event._id, sourceEventIds });
    }
  };

  if (isRejected) {
    return <></>;
  }

  return (
    <Card>
      <div>
        <Card.Header
          avatar={
            event.img ? (
              <img
                src={withCacheBust(event.img, imageCacheBust)}
                alt={event.title}
                height={60}
                style={{ borderRadius: 4 }}
              />
            ) : undefined
          }
          title={event.title}
          tagline={[
            isApproved ? 'Approved' : isDynamic ? 'Dynamic' : 'Pending',
            sourceEventIds.length > 1 ? `${sourceEventIds.length} sources` : '',
            event.confidenceLevel ?? '',
          ]
            .filter(Boolean)
            .join(' | ')}
          subtitle={event.timeDetails}
          action={
            canAct ? (
              <IconButton
                color="danger"
                variant="smooth"
                disabled={isRejectingDynamicEvent}
                onClick={handleReject}
              >
                <FontAwesomeIcon name="times" />
              </IconButton>
            ) : undefined
          }
        />
        {canAct && (
          <Card.Body>
            <div>
              <div style={{ fontSize: 12, fontWeight: 500 }}>Action</div>
              <MultipleChoiceList
                options={ACTION_OPTIONS}
                selected={[selectedAction]}
                onChange={([updatedAction]) =>
                  handleSelectAction(updatedAction as DynamicEventActionCardAction)
                }
              />
            </div>

            {selectedAction === DynamicEventActionCardAction.Approve && (
              <ApproveEventForm
                event={event}
                actionToken={actionToken}
                isDynamic={isDynamic}
                sourceEventIds={sourceEventIds}
                tagIds={tagIds}
                seriesOptions={seriesOptions}
                onComplete={handleFinalized}
              />
            )}

            {selectedAction === DynamicEventActionCardAction.Reject && (
              <div className="mt-4">
                <Button
                  roundness="rounded"
                  color="danger"
                  disabled={isRejectingDynamicEvent}
                  onClick={handleReject}
                >
                  Reject
                </Button>
              </div>
            )}
          </Card.Body>
        )}
        {/* With a single source there's no other image to switch to */}
        {!canAct && sourceEventIds.length > 1 && (
          <Card.Body>
            <UpdateEventImageForm
              event={event}
              sourceEventIds={sourceEventIds}
              onUpdated={() => setImageCacheBust(Date.now())}
            />
          </Card.Body>
        )}
      </div>
    </Card>
  );
};

export const SeriesManageEvents = ({ tagIds, cityId }: { tagIds: string[]; cityId: string }) => {
  const { eventsAreReady, events = [] } = useUnauthorizedEventsQuery(
    {
      tagIds,
      startDate: dayjs().format('YYYY-MM-DD'),
      forceSupply: 'true',
    },
    Boolean(tagIds?.length),
    {
      refetchOnMount: true,
      refetchOnReconnect: true,
      refetchOnWindowFocus: true,
    },
  );

  const { series = [], isFetched: isSeriesFetched } = useSeries();
  const seriesOptions = useMemo(
    () =>
      uniqBy(
        series
          .filter(({ slug }) =>
            ['denver-drag', 'denver-queer-calendar', 'queer-comedy-colorado'].includes(slug),
          )
          .map(({ _id, title }) => ({ label: title, value: _id })),
        'value',
      ),
    [series],
  );

  // Merged sources live in the database, so look them up for every non-dynamic event at once
  const listedEventIds = useMemo(
    () => events.filter(({ actionToken }) => !actionToken).map(({ _id }) => _id),
    [events],
  );
  const { sourceEventIdsByEventId } = useSourceEventIdsQuery(
    listedEventIds,
    eventsAreReady && listedEventIds.length > 0,
  );

  // Wait for series too, since each card preselects its series once on mount
  if (!eventsAreReady || !isSeriesFetched) {
    return <div>Loading...</div>;
  }

  if (!events.length) {
    return <EmptyState title="No events" content="No upcoming events for this series." />;
  }

  return (
    <div className="d-grid">
      {events.map((event) => (
        <DynamicEventActionCard
          key={event._id}
          event={event}
          tagIds={tagIds}
          seriesOptions={seriesOptions}
          mergedSourceEventIds={sourceEventIdsByEventId?.[event._id]}
        />
      ))}
    </div>
  );
};
